<template>
  <div class="mt-4">
    <v-flex d-flex align-center>
      <v-subheader class="font-weight-bold">
        <span id="system-log__rows">
          <span id="system-log__total-string">Total:</span>
          <span id="system-log__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
      <v-btn small color="primary" class="ml-2 mr-2" @click="refresh">
        <v-icon small left>mdi-arrow-up</v-icon>
        {{ $t("modules.logs.top") }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        dense
        class="mr-2"
        height="31"
        :label="$t('actions.searchDevice')"
        hide-details
        style="width: 50px"
      ></v-text-field>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="dateRange"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            dense
            class="mr-2"
            height="31"
            v-model="dateRange"
            :label="$t('modules.logs.systemLog.dateRange')"
            prepend-inner-icon="mdi-calendar"
            readonly
            style="width: 50px"
            hide-details
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          color="primary"
          v-model="dateRange"
          no-title
          scrollable
          range
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false">
            {{ $t("actions.cancel") }}
          </v-btn>
          <v-btn text color="primary" @click="$refs.menu.save(dateRange)">
            {{ $t("actions.ok") }}
          </v-btn>
        </v-date-picker>
      </v-menu>
      <v-autocomplete
        dense
        height="30"
        v-model="device"
        :items="devicesFilters"
        :label="$t('modules.logs.systemLog.devices')"
        style="width: 50px"
        hide-details
        class="mr-2"
        multiple
        :search-input.sync="deviceSearch"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0" small>
            <span>{{
              item.text.length > 15
                ? item.text.substring(0, 15) + "..."
                : item.text
            }}</span>
          </v-chip>
          <span v-if="index === 1" class="grey--text caption">
            (+{{ device.length - 1 }})
          </span>
        </template>
      </v-autocomplete>
      <v-select
        dense
        height="30"
        v-model="severity"
        :items="severityFilters"
        :label="$t('modules.logs.systemLog.severity')"
        style="width: 50px"
        class="mr-2"
        hide-details
        multiple
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0" small>
            <span>{{ item.text }}</span>
          </v-chip>
          <span v-if="index === 1" class="grey--text caption">
            (+{{ severity.length - 1 }})
          </span>
        </template>
      </v-select>
      <v-select
        dense
        height="30"
        v-model="deviceAttributeTypes"
        :items="deviceAttributeTypesFilter"
        :label="$t('modules.logs.systemLog.deviceAttributeType')"
        style="width: 50px"
        class="mr-2"
        hide-details
        multiple
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0" small>
            <span>{{ item.text }}</span>
          </v-chip>
          <span v-if="index === 1" class="grey--text caption">
            (+{{ deviceAttributeTypes.length - 1 }})
          </span>
        </template>
      </v-select>
      <v-btn small class="mr-2" @click="filter" :disabled="menu">{{
        $t("actions.filter")
      }}</v-btn>
      <v-btn small class="mr-2" @click="clearFilter">{{
        $t("actions.clear")
      }}</v-btn>
      <v-btn
        small
        class="ml-2 mr-2"
        color="primary"
        outlined
        @click="downloadCSV"
        :loading="isDownloading"
        :disabled="menu"
        >{{ $t("actions.downloadCSV") }}
      </v-btn>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      class="elevation-1 mx-2 mt-3 scroll"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      style="min-height: 100%;"
      id="system-log__table"
      fixed-header
      :height="height - 15 + 'px'"
    >
      <template v-slot:body.append>
        <td :colspan="headers.length" class="text-center py-2" v-if="loading">
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template v-slot:item.log_time="props">
        <span>{{ parseLogDatetime(new Date(props.item.log_time)) }}</span>
      </template>
      <template v-slot:item.device_details.device_name="props">
        <span>{{ props.item.device_details.device_name }}</span>
      </template>
      <template v-slot:item.device_attribute_type="props">
        <span>{{
          util.parseDeviceAttributeType(props.item.device_attribute_type)
        }}</span>
      </template>
      <template v-slot:item.severity="props">
        <v-chip
          small
          :color="logsUtil.parseColor(props.item.severity).toString()"
          text-color="logs__severity_chip_color"
          >{{ logsUtil.parseSeverity(props.item.severity) }}</v-chip
        >
      </template>
      <template v-slot:item.action="props">
        <v-btn
          icon
          :disabled="props.item.device_attribute_type === 12"
          @click="
            loadDeviceLogs(
              props.item,
              props.item.device_attribute_type,
              props.item.log_time
            )
          "
        >
          <v-icon>mdi-information</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <system-log-dialog></system-log-dialog>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import SystemLogDialog from "./system-log-dialog";
import util from "@/modules/logs/system-log/util";
import { logMixins } from "@/modules/logs/mixins";
import logsUtil from "@/modules/logs/util";

const fileTitle = "system_logs";

export default {
  name: "SystemLogTable",
  components: { SystemLogDialog },
  mixins: [logMixins],
  data() {
    return {
      util,
      logsUtil,
      deviceSearch: "",
      dateRange: [],
      severity: [],
      device: [],
      headers: [
        {
          text: this.$t("modules.logs.systemLog.logTime"),
          align: "left",
          value: "log_time"
        },
        {
          text: this.$t("modules.logs.systemLog.deviceName"),
          align: "start",
          value: "device_details.device_name"
        },
        {
          text: this.$t("modules.logs.systemLog.serialNumber"),
          align: "center",
          value: "device_details.device_serial"
        },
        {
          text: this.$t("modules.logs.systemLog.severity"),
          align: "start",
          value: "severity"
        },
        {
          text: this.$t("modules.logs.systemLog.deviceAttributeType"),
          align: "start",
          value: "device_attribute_type"
        },
        {
          text: this.$t("modules.logs.systemLog.eventDescription"),
          align: "start",
          value: "event_description",
          width: "40%"
        },
        {
          text: "",
          align: "center",
          value: "action"
        }
      ],
      severityFilters: [
        {
          text: this.$t("modules.logs.severity.normal"),
          value: 1
        },
        {
          text: this.$t("modules.logs.severity.minor"),
          value: 2
        },
        {
          text: this.$t("modules.logs.severity.major"),
          value: 3
        },
        {
          text: this.$t("modules.logs.severity.critical"),
          value: 4
        }
      ],
      deviceAttributeTypesFilter: [
        {
          text: this.$t("modules.logs.systemLog.temperature"),
          value: 1
        },
        {
          text: this.$t("modules.logs.systemLog.deviceState"),
          value: 2
        },
        {
          text: this.$t("modules.logs.systemLog.cellularPort1"),
          value: 3
        },
        {
          text: this.$t("modules.logs.systemLog.cellularPort2"),
          value: 4
        },
        {
          text: this.$t("modules.logs.systemLog.cellularPort3"),
          value: 5
        },
        {
          text: this.$t("modules.logs.systemLog.cellularPort4"),
          value: 6
        },
        {
          text: this.$t("modules.logs.systemLog.ethPort1"),
          value: 7
        },
        {
          text: this.$t("modules.logs.systemLog.ethPort2"),
          value: 8
        },
        {
          text: this.$t("modules.logs.systemLog.wifi"),
          value: 9
        },
        {
          text: this.$t("modules.logs.systemLog.vlan"),
          value: 10
        },
        {
          text: this.$t("modules.logs.systemLog.simEvent"),
          value: 12
        }
      ],
      deviceAttributeTypes: [],
      paginateParams: {
        object_id: 0,
        device_name: null,
        device_ids: [],
        start_date: null,
        end_date: null,
        severities: [],
        device_attribute_types: [],
        page_size: null
      },
      isDownloading: false,
      editedIndex: -1
    };
  },

  computed: {
    search: {
      get() {
        return this.$store.state.systemLog.search || "";
      },
      set(val) {
        this.$store.commit("systemLog/updateSearch", val);
      }
    },
    selected: {
      get() {
        return this.$store.state.systemLog.selected;
      },
      set(val) {
        this.$store.commit("systemLog/updateSelected", val);
      }
    },
    dialog: {
      get() {
        return this.$store.state.systemLog.dialog;
      },
      set(val) {
        this.$store.commit("systemLog/updateDialog", val);
      }
    },
    dialogLoading: {
      get() {
        return this.$store.state.systemLog.dialogLoading;
      },
      set(val) {
        this.$store.commit("systemLog/updateDialogLoading", val);
      }
    },
    systemLog() {
      return this.$store.state.systemLog.systemLog;
    },
    devicesFilters() {
      return this.$store.state.devices.devices.map(item => {
        return {
          text: item.name,
          value: item.id
        };
      });
    }
  },
  watch: {
    dateRange(val) {
      if (val && val.length === 2) {
        if (new Date(val[0]) > new Date(val[1])) {
          this.dateRange = [val[1], val[0]];
        }
      }
    },
    device() {
      this.deviceSearch = "";
    }
  },
  beforeDestroy() {
    this.clearFilter();
  },
  methods: {
    loadDeviceLogs(item, attributeType, logTime) {
      this.dialog = true;
      this.dialogLoading = true;
      this.selected = item;
      this.$store
        .dispatch("systemLog/getDeviceLog", {
          id: item.device_id,
          attributeType: attributeType,
          logTime: util.parseDate(logTime)
        })
        .then(response => {
          let data = response.data.system_logs;
          if (data.length === 0) {
            this.$store.commit("systemLog/updateQueryId", {
              nextQueryId: null,
              previousQueryId: []
            });
            return;
          }
          let nextQueryId = data[data.length - 1].id;
          this.$store.commit("systemLog/updateQueryId", {
            nextQueryId,
            previousQueryId: []
          });
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.dialogLoading = false;
        });
    },

    initialize() {
      this.loading = true;
      this.$store
        .dispatch("systemLog/fetchAll", this.paginateParams)
        .then(() => {
          if (this.systemLog.length > 0) {
            this.loadItems = [...this.loadItems, ...this.systemLog];
            this.options.itemsPerPage = this.loadItems.length;
            if (this.loadItems.length === 0) {
              this.isLastPage = true;
              return;
            }
            this.paginateParams.object_id = this.loadItems[
              this.loadItems.length - 1
            ].id;
            this.isLastPage =
              this.systemLog.length < this.defaultPageSize ? true : false;
          } else {
            this.isLastPage = true;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    filter() {
      this.paginateParams = {
        object_id: 0,
        device_name: this.search !== "" ? this.search : null,
        device_ids: this.device.length > 0 ? JSON.stringify(this.device) : null,
        start_date: logsUtil.parseDateTime(this.dateRange[0], 0),
        end_date: logsUtil.parseDateTime(this.dateRange[1], 24),
        severities:
          this.severity.length > 0 ? JSON.stringify(this.severity) : null,
        device_attribute_types:
          this.deviceAttributeTypes.length > 0
            ? JSON.stringify(this.deviceAttributeTypes)
            : null,
        page_size: null
      };
      this.loadItems = [];
      this.initialize();
    },

    clearFilter() {
      this.search = "";
      this.device = [];
      this.severity = [];
      this.dateRange = [];
      this.deviceAttributeTypes = [];
      this.loadItems = this.systemLog;

      this.paginateParams = {
        object_id: 0,
        device_names: null,
        device_ids: null,
        start_date: null,
        end_date: null,
        severities: null,
        device_attribute_types: null,
        page_size: null
      };
      this.loadItems = [];
      this.initialize();
    },

    downloadCSV() {
      this.isDownloading = true;
      let downloadParams = {
        object_id: 0,
        device_name: this.search !== "" ? this.search : null,
        device_ids: this.device.length > 0 ? JSON.stringify(this.device) : null,
        start_date: logsUtil.parseDateTime(this.dateRange[0], 0),
        end_date: logsUtil.parseDateTime(this.dateRange[1], 24),
        severities:
          this.severity.length > 0 ? JSON.stringify(this.severity) : null,
        device_attribute_types:
          this.deviceAttributeTypes.length > 0
            ? JSON.stringify(this.deviceAttributeTypes)
            : null,
        page_size: this.downloadPageSize
      };
      this.$store
        .dispatch("systemLog/fetchToDownload", downloadParams)
        .then(response => {
          let data = response.data.system_logs;
          const formattedLogs = data.map(systemLog => {
            return {
              log_time: this.removeComma(
                this.parseLogDatetime(new Date(systemLog.log_time))
              ),
              device_name: this.removeComma(
                systemLog.device_details.device_name
              ),
              device_serial: this.removeComma(
                systemLog.device_details.device_serial
              ),
              severity: this.removeComma(
                this.logsUtil.parseSeverity(systemLog.severity)
              ),
              device_attribute_type: this.removeComma(
                this.util.parseDeviceAttributeType(
                  systemLog.device_attribute_type
                )
              ),
              event_description: `"${systemLog.event_description}"`
            };
          });
          this.exportCSVFile(util.csvHeaders, formattedLogs, fileTitle);
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.isDownloading = false;
        });
    }
  }
};
</script>
